import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Control } from "react-hook-form";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";

import type { UserCrew } from "src/shared/api-types/user";

import type { UploadableCourse } from "src/features/bulk-certificate-upload";

import { StyledPaper } from "../Base";
import { AutocompleteField, AutocompleteMultiField } from "../FormRhf";
import { CertificateDetailsForm } from "./CertificateDetailsForm";
import {
  useUserCoursesCertificateForm,
  type UserCoursesFormInput,
  type UserCoursesSubmitPayload,
} from "./useUserCoursesCertificateForm";

export interface UserCoursesBulkUploadProps {
  crewId: number;
  courses: UploadableCourse[];
  users: UserCrew[];
  preselectedUserId?: number;
  onSubmit: (values: UserCoursesSubmitPayload) => Promise<string | null>;
}

const STEPS = ["personStep", "certificatesStep", "uploadStep"] as const;

const getCourseLabel = (course: UploadableCourse) =>
  `(${course.isCustom ? `cc_${course.id}` : course.id}) ${course.name}`;

const UserCoursesBulkUpload = ({ crewId, courses, users, preselectedUserId, onSubmit }: UserCoursesBulkUploadProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);

  const { form, validityHook, neverExpires, periodsResult, getTooltipContent } = useUserCoursesCertificateForm(crewId);

  const {
    control: _control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isValid },
  } = form;

  const control = _control as unknown as Control<UserCoursesFormInput, any, any>;
  const selectedUser = watch("selectedUser") ?? null;
  const selectedCourses = watch("courses") ?? [];
  const watchedFiles = watch("files") ?? [];
  const completedDate = watch("completed") ?? "";
  const selectedType = selectedCourses[0]?.isCustom ?? null;

  const userCourses = useMemo(
    () => (selectedUser ? courses.filter((course) => course.userIds.includes(selectedUser.id)) : []),
    [courses, selectedUser],
  );

  const courseOptions = useMemo(
    () => userCourses.filter((course) => selectedType === null || course.isCustom === selectedType),
    [selectedType, userCourses],
  );

  useEffect(() => {
    if (!preselectedUserId || selectedUser) return;
    const user = users.find((candidate) => candidate.id === preselectedUserId);
    if (!user) return;
    setValue("selectedUser", user, { shouldValidate: true, shouldDirty: true });
    setActiveStep(1);
    setMaxVisitedStep(1);
  }, [preselectedUserId, selectedUser, setValue, users]);

  const stepComplete = [!!selectedUser, selectedCourses.length > 0, !!completedDate && watchedFiles.length > 0];

  const goToStep = (step: number) => {
    setActiveStep(step);
    setMaxVisitedStep((prev) => Math.max(prev, step));
  };

  const handleUserChange = () => {
    setValue("courses", [], { shouldValidate: true, shouldDirty: true });
  };

  const handleCoursesChange = (newValue: UploadableCourse[]) => {
    const firstType = newValue[0]?.isCustom;
    if (firstType === undefined) return;
    const compatible = newValue.filter((course) => course.isCustom === firstType);
    if (compatible.length !== newValue.length) {
      setValue("courses", compatible, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleNext = async () => {
    if (activeStep === 1) {
      await validityHook.fetchPeriods();
    }
    goToStep(activeStep + 1);
  };

  const submit = async (values: UserCoursesSubmitPayload) => {
    const error = await onSubmit(values);
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    } else {
      enqueueSnackbar(t("certificate_upload.bulk.success"), { variant: "success" });
      window.history.back();
    }
  };

  return (
    <StyledPaper variant="outlined">
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Stepper nonLinear alternativeLabel activeStep={activeStep}>
          {STEPS.map((step, index) => (
            <Step key={step} completed={stepComplete[index]}>
              <StepButton
                onClick={() => goToStep(index)}
                disabled={index > maxVisitedStep && !stepComplete[index - 1]}
                // MUI 9 makes the Stepper a tablist with one Tab stop on the first enabled step; keep it
                // on the current step, as before.
                tabIndex={index === activeStep ? 0 : -1}
              >
                {t(`certificate_upload.bulk.single_user.${step}`)}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ maxWidth: 600 }}>
            <AutocompleteField
              control={control}
              name="selectedUser"
              options={users}
              getOptionLabel={(user) => user.fullname}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              label={`${t("certificate_upload.bulk.single_user.select_person")}*`}
              onChangeEffect={handleUserChange}
            />
          </Box>
        )}

        {activeStep === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              maxWidth: 600,
            }}
          >
            {selectedUser && (
              <Typography variant="h4" component="h3">
                {selectedUser.fullname}
              </Typography>
            )}
            <AutocompleteMultiField
              control={control}
              name="courses"
              options={courseOptions}
              getOptionLabel={getCourseLabel}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              groupBy={(course) =>
                course.isCustom
                  ? t("certificate_upload.bulk.custom_certificates")
                  : t("certificate_upload.bulk.standard_courses")
              }
              label={`${t("certificate_upload.bulk.single_user.select_certificates")}*`}
              minRows={4}
              disableCloseOnSelect
              onChangeEffect={handleCoursesChange}
            />
            {selectedType !== null && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t(
                  selectedType
                    ? "certificate_upload.bulk.single_user.custom_only_hint"
                    : "certificate_upload.bulk.single_user.standard_only_hint",
                )}
              </Typography>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <CertificateDetailsForm
            control={control}
            neverExpires={neverExpires}
            showWarningIcon={!!periodsResult.divergentPeriods}
            getTooltipContent={() => getTooltipContent(t)}
            courseLabel={selectedCourses.length > 0 ? selectedCourses.map(getCourseLabel).join(", ") : undefined}
            usersLabel={selectedUser?.fullname}
          />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            {activeStep > 0 && (
              <Button variant="outlined" onClick={() => goToStep(activeStep - 1)}>
                {t("back")}
              </Button>
            )}
            {activeStep < 2 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!stepComplete[activeStep] || (activeStep === 1 && validityHook.fetching)}
                startIcon={activeStep === 1 && validityHook.fetching ? <CircularProgress size={16} /> : undefined}
              >
                {t("next")}
              </Button>
            )}
          </Box>

          <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
            {t("certificate_upload.bulk.submit")}
          </Button>
        </Box>
      </Box>
    </StyledPaper>
  );
};

export default UserCoursesBulkUpload;
